import React, { useMemo, useState } from "react";
import { useLocalization } from "@fluent/react";
import {
  Button,
  Chip,
  CollapsibleCard,
  IconButton,
  Row,
  TextInput,
} from "@narmi/design_system";
import cc from "classcat";
import { groupAccountsByOrganization } from "byzantine";
import Account from "byzantine/src/Account";
import type { Features } from "byzantine/src/Feature";
import type { Role } from "byzantine/src/Role";
import AccountPermissionsSummary from "./AccountPermissionsSummary";
import LongRoleTile from "./LongRoleTile";
import {
  countAccountPermissions,
  getRoleAccountEntries,
  hasSamePermissionsAcrossAccounts,
  type RoleAccountEntry,
} from "./RoleDisplayUtils";
import styles from "./OrganizationRoles.module.scss";

// Same threshold as the role form: longer lists get a search box and show the first rows only.
const LONG_ACCOUNT_LIST_THRESHOLD = 10;

interface RoleAccountPermissionsViewProps {
  /** Role detail, so `account_permissions` is present (or `null` for a legacy role). */
  role: Role;
  /** Every account the user can see; the role's entries are picked from these. */
  accounts: API.Account[];
  institutionName?: string;
  /** Subtitle for the external transfer row, when the role is limited to some external accounts. */
  externalTransferSubtitle?: string;
  /** Institution features: permissions it doesn't offer are left out (Admin holds them all). */
  features?: Features;
  /** Summary shown above the accounts, e.g. "Full access" for Admin. */
  summaryTile?: React.ReactNode;
}

type DisplayEntry = RoleAccountEntry<Account>;

/**
 * Per-account permissions of a role: a row per account that expands into its money movement and
 * manage template permissions. Shared by every surface that describes a role.
 */
export default function RoleAccountPermissionsView({
  role,
  accounts,
  institutionName,
  externalTransferSubtitle,
  features,
  summaryTile,
}: RoleAccountPermissionsViewProps) {
  const { l10n } = useLocalization();
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [openAccountIds, setOpenAccountIds] = useState<Set<string>>(new Set());

  const entries = useMemo<DisplayEntry[]>(
    () =>
      getRoleAccountEntries(
        role,
        accounts.map((account) => Account.deserialize(account))
      ),
    [role, accounts]
  );
  const isLongList = entries.length > LONG_ACCOUNT_LIST_THRESHOLD;
  const isSame = hasSamePermissionsAcrossAccounts(entries);

  const groupedAccounts = useMemo(
    () =>
      groupAccountsByOrganization(
        entries.map((entry) => entry.account),
        institutionName
      ) as unknown as Map<string, Account[]>,
    [entries, institutionName]
  );
  const orgCount = groupedAccounts.size;
  const hasMultipleOrgs = orgCount > 1;

  const normalizedQuery = query.trim().toLowerCase();
  const matches = ({ account }: DisplayEntry) =>
    !normalizedQuery ||
    account.getShortDescription().toLowerCase().includes(normalizedQuery);

  const setOpen = (accountId: string, open: boolean) =>
    setOpenAccountIds((previous) => {
      const next = new Set(previous);
      if (open) next.add(accountId);
      else next.delete(accountId);
      return next;
    });

  const renderAccount = ({ account, state }: DisplayEntry) => {
    const accountId = String(account.id);
    const isViewOnly = state.access === "view_only";
    const count = countAccountPermissions(state.permissions, features);
    // View only accounts, and move money accounts without a permission, have nothing to expand.
    const canExpand = !isViewOnly && count > 0;
    const isOpen = canExpand && openAccountIds.has(accountId);

    return (
      <div key={accountId}>
        <CollapsibleCard
          isOpen={isOpen}
          trigger="caret-end"
          onOpen={() => setOpen(accountId, true)}
          onClose={() => setOpen(accountId, false)}
          renderTitle={() => (
            <div
              className={cc([
                styles.accountCardHeader,
                { [styles.accountCardHeaderOpen]: isOpen },
              ])}
            >
              <span className={styles.accountLabel}>
                <span className={styles.accountBankIcon} aria-hidden="true">
                  <span className="narmi-icon-bank" />
                </span>
                <h4 className={styles.accountName}>{account.getName()}</h4>
                <span className={styles.accountMask}>
                  {account.getMaskedNumber()}
                </span>
              </span>
              <span className={styles.accountStatusChip}>
                <Chip
                  kind="success"
                  label={
                    isViewOnly
                      ? l10n.getString("org-role-account-view-only")
                      : l10n.getString("org-role-account-permission-count", {
                          count,
                        })
                  }
                />
              </span>
              {canExpand && (
                <span className={styles.accountChevron}>
                  <IconButton
                    type="button"
                    kind="action"
                    label={isOpen ? "Collapse" : "Expand"}
                    name={isOpen ? "chevron-up" : "chevron-down"}
                    textSize="l"
                    onClick={() => setOpen(accountId, !isOpen)}
                  />
                </span>
              )}
            </div>
          )}
        >
          <AccountPermissionsSummary
            permissions={state.permissions}
            features={features}
            externalTransferSubtitle={externalTransferSubtitle}
          />
        </CollapsibleCard>
      </div>
    );
  };

  const filtered = entries.filter(matches);
  // Long lists start at the first rows; searching shows every match.
  const isCollapsed = isLongList && !showAll && !normalizedQuery;
  let remaining = isCollapsed ? LONG_ACCOUNT_LIST_THRESHOLD : Infinity;
  const takeVisible = (list: DisplayEntry[]) => {
    const visible = list.slice(0, remaining);
    remaining -= visible.length;
    return visible;
  };

  const groups: [string, DisplayEntry[]][] = hasMultipleOrgs
    ? Array.from(groupedAccounts.entries()).map(([orgName, orgAccounts]) => [
        orgName,
        filtered.filter((entry) => orgAccounts.includes(entry.account)),
      ])
    : [["", filtered]];
  const visibleGroups = groups
    .map(([orgName, list]) => [orgName, takeVisible(list)] as const)
    .filter(([, list]) => list.length > 0);

  return (
    <div>
      <Row gapSize="xs" alignItems="center">
        <Row.Item>
          <div
            className={`${styles.usersSectionHeader} fontSize--xs fontWeight--bold`}
          >
            {l10n.getString("org-role-account-permissions")}
          </div>
        </Row.Item>
        {isSame && (
          <Row.Item shrink>
            <div style={{ marginBottom: "var(--space-s)" }}>
              <Chip
                kind="primary"
                startIcon="check"
                label={l10n.getString("org-role-same-across-n-accounts", {
                  count: entries.length,
                })}
              />
            </div>
          </Row.Item>
        )}
      </Row>
      {isLongList && (
        <div className={styles.accountsToolbar}>
          <div className={styles.accountsSearch}>
            <TextInput
              label={l10n.getString("org-role-accounts-search-placeholder")}
              value={query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setQuery(e.target.value)
              }
              startIcon="search"
            />
          </div>
          <span className={styles.accountsCount}>
            {hasMultipleOrgs
              ? l10n.getString("org-role-accounts-count-multi-org", {
                  count: entries.length,
                  orgs: orgCount,
                })
              : l10n.getString("org-role-accounts-count", {
                  count: entries.length,
                })}
          </span>
        </div>
      )}
      {summaryTile && (
        <div className={styles.accountSummaryTile}>{summaryTile}</div>
      )}
      {entries.length === 0 && !summaryTile && (
        <LongRoleTile
          iconClass="narmi-icon-bank"
          title={l10n.getString("org-role-account-none-title")}
          description={l10n.getString("org-role-account-none-description")}
        />
      )}
      {visibleGroups.map(([orgName, list]) => (
        <div key={orgName || "accounts"} className={styles.accountGroup}>
          {orgName && <div className={styles.orgGroupName}>{orgName}</div>}
          <div className={styles.accountsList}>{list.map(renderAccount)}</div>
        </div>
      ))}
      {entries.length > 0 && filtered.length === 0 && (
        <div className={styles.noResults}>
          {l10n.getString("org-role-accounts-no-results")}
        </div>
      )}
      {isLongList && !normalizedQuery && (
        <div style={{ marginTop: "16px" }}>
          <Button
            kind="plain"
            onClick={() => setShowAll((previous) => !previous)}
            endIcon={showAll ? "chevron-up" : "chevron-down"}
          >
            {showAll
              ? l10n.getString("org-role-button-see-less")
              : l10n.getString("org-role-button-view-all")}
          </Button>
        </div>
      )}
    </div>
  );
}
